import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Trophy, Target, Clock, Download, FileText, Gift, Image as ImgIcon } from 'lucide-react'
import api from '../api/client'

function embedVideo(url) {
  if (!url) return null
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`
  const vm = url.match(/vimeo\.com\/(\d+)/)
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`
  return null
}

function useCountdown(fim) {
  const [t, setT] = useState(null)
  useEffect(() => {
    if (!fim) return
    const alvo = new Date(fim + 'T23:59:59').getTime()
    const tick = () => {
      const diff = alvo - Date.now()
      if (diff <= 0) { setT({ d: 0, h: 0, m: 0 }); return }
      setT({ d: Math.floor(diff / 864e5), h: Math.floor((diff % 864e5) / 36e5), m: Math.floor((diff % 36e5) / 6e4) })
    }
    tick(); const id = setInterval(tick, 30000); return () => clearInterval(id)
  }, [fim])
  return t
}

export default function CampanhaDetalhe() {
  const { id } = useParams()
  const nav = useNavigate()
  const [c, setC] = useState(null)
  const [erro, setErro] = useState(false)
  const cd = useCountdown(c?.data_fim)

  useEffect(() => { api.get(`/incentivos/campanhas/${id}`).then(({ data }) => setC(data)).catch(() => setErro(true)) }, [id])
  const baixar = (dl) => { api.post(`/incentivos/campanhas/${id}/metric?tipo=download`).catch(() => {}); window.open(dl.url, '_blank') }

  if (erro) return <div className="max-w-3xl mx-auto mt-10 text-center text-neutral-500">Campanha não encontrada.</div>
  if (!c) return <div className="max-w-3xl mx-auto mt-10 text-center text-neutral-400">Carregando...</div>

  const embed = embedVideo(c.video_url)
  const prog = c.progresso?.pct ?? null

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <button onClick={() => nav('/incentivos')} className="text-sm text-neutral-500 hover:text-graphite inline-flex items-center gap-1"><ArrowLeft size={15} /> Campanhas</button>

      {/* Banner + contador */}
      <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-sm bg-white">
        {(c.banner_url || c.imagem_url) && (
          <img src={c.banner_url || c.imagem_url} alt={c.nome} className="w-full max-h-72 object-cover" />
        )}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-heading font-extrabold text-graphite">{c.nome}</h1>
              {c.subtitulo && <p className="text-gray-500">{c.subtitulo}</p>}
            </div>
            {cd && (
              <div className="flex gap-2">
                {[['Dias', cd.d], ['Horas', cd.h], ['Min', cd.m]].map(([l, v]) => (
                  <div key={l} className="bg-accent-50 border border-accent-200 rounded-xl px-3 py-1.5 text-center min-w-[54px]">
                    <p className="text-lg font-heading font-extrabold text-accent-600 leading-none">{v}</p>
                    <p className="text-[10px] text-neutral-500 uppercase">{l}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          {prog != null && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-neutral-500 mb-1"><span>Seu progresso</span><span>{prog}%</span></div>
              <div className="w-full h-2.5 bg-neutral-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${prog}%`, background: 'linear-gradient(90deg,#ffcc00,#ff6c00)' }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[[Gift, 'Premiação', c.premio || '—'], [Target, 'Meta', c.meta_descricao || '—'],
          [Clock, 'Período', c.data_fim ? `até ${new Date(c.data_fim).toLocaleDateString('pt-BR')}` : '—'],
          [Trophy, 'Vagas', c.vagas ?? '—']].map(([Ic, l, v]) => (
          <div key={l} className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-4">
            <div className="flex items-center gap-1.5 text-[11px] text-neutral-500"><Ic size={13} className="text-accent-500" /> {l}</div>
            <p className="font-semibold text-graphite text-sm mt-1">{v}</p>
          </div>
        ))}
      </div>

      {c.descricao && <Secao titulo="Descrição"><p className="text-sm text-neutral-700 whitespace-pre-line">{c.descricao}</p></Secao>}

      {embed && (
        <Secao titulo="Vídeo">
          <div className="aspect-video rounded-xl overflow-hidden"><iframe src={embed} title="Vídeo" className="w-full h-full" allowFullScreen frameBorder="0" /></div>
        </Secao>
      )}

      {(c.galeria || []).length > 0 && (
        <Secao titulo="Galeria" icon={ImgIcon}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {c.galeria.map((g, i) => <img key={i} src={g} alt="" className="rounded-lg w-full h-32 object-cover" />)}
          </div>
        </Secao>
      )}

      {c.regulamento && <Secao titulo="Regulamento" icon={FileText}><p className="text-sm text-neutral-700 whitespace-pre-line">{c.regulamento}</p></Secao>}

      {(c.downloads || []).length > 0 && (
        <Secao titulo="Downloads" icon={Download}>
          <div className="space-y-2">
            {c.downloads.map((dl, i) => (
              <button key={i} onClick={() => baixar(dl)} className="w-full flex items-center gap-2 bg-neutral-50 hover:bg-accent-50 rounded-lg px-3 py-2.5 text-sm text-left">
                <Download size={15} className="text-accent-500" /> <span className="flex-1">{dl.nome || dl.url}</span>
              </button>
            ))}
          </div>
        </Secao>
      )}
    </div>
  )
}

function Secao({ titulo, icon: Ic, children }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5">
      <h2 className="font-heading font-bold text-graphite text-sm mb-3 flex items-center gap-1.5">{Ic && <Ic size={15} className="text-accent-500" />} {titulo}</h2>
      {children}
    </div>
  )
}
